import React, { useState, useEffect } from 'react';
import { 
  Gift, Settings, Plus, Trash2, RotateCcw, CheckCircle, 
  AlertCircle, Coins, Users, Clock, ShieldCheck, Search, Eye, X, ArrowRight
} from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const LuckyBagManagement = () => {
  const [activeTab, setActiveTab] = useState('config'); // 'config' | 'audit'
  
  // Dynamic Configuration State
  const [amounts, setAmounts] = useState([1999, 9999, 29999, 99999]);
  const [slots, setSlots] = useState([10, 20, 40, 60]);
  const [feePercent, setFeePercent] = useState(20);
  const [newAmountInput, setNewAmountInput] = useState('');
  const [newSlotInput, setNewSlotInput] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [configSuccessMsg, setConfigSuccessMsg] = useState('');
  const [configErrorMsg, setConfigErrorMsg] = useState('');

  // Summary State
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Live Audit Bags List State
  const [bags, setBags] = useState([]);
  const [bagsLoading, setBagsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1, totalItems: 0 });

  // Detailed Modal State
  const [selectedBagDetail, setSelectedBagDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token') || '';
    return {
      headers: {
        Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`
      }
    };
  };

  useEffect(() => {
    fetchConfig();
    fetchSummary();
  }, []);

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchBags();
    }
  }, [activeTab, page, statusFilter]);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/admin/lucky-bags/config`, getAuthHeaders());
      if (res.data?.success && res.data?.data) {
        setAmounts(res.data.data.amounts || [1999, 9999, 29999, 99999]);
        setSlots(res.data.data.slots || [10, 20, 40, 60]);
        setFeePercent(res.data.data.fee_percentage !== undefined ? res.data.data.fee_percentage : 20);
      }
    } catch (err) {
      console.error("Error loading lucky bag config:", err);
    }
  };

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/admin/lucky-bags/summary`, getAuthHeaders());
      if (res.data?.success && res.data?.data) {
        setSummary(res.data.data);
      }
    } catch (err) {
      console.error("Error loading lucky bag summary:", err);
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchBags = async () => {
    setBagsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '15'
      });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery.trim()) params.append('bag_id', searchQuery.trim());

      const res = await axios.get(`${constant.API_BASE_URL}/admin/lucky-bags?${params.toString()}`, getAuthHeaders());
      if (res.data?.success) {
        setBags(res.data.data || []);
        setPagination({
          totalPages: res.data.pagination?.totalPages || 1,
          totalItems: res.data.pagination?.totalItems || 0
        });
      }
    } catch (err) {
      console.error("Error loading bags audit list:", err);
    } finally {
      setBagsLoading(false);
    }
  };

  const handleAddAmount = () => {
    const val = parseInt(newAmountInput.trim(), 10);
    if (!val || val <= 0) return;
    if (amounts.includes(val)) {
      setConfigErrorMsg(`Gold amount ${val} already exists!`);
      return;
    }
    const updated = [...amounts, val].sort((a, b) => a - b);
    setAmounts(updated);
    setNewAmountInput('');
    setConfigErrorMsg('');
  };

  const handleRemoveAmount = (valToRemove) => {
    if (amounts.length <= 1) {
      setConfigErrorMsg("At least one Gold amount preset must be configured.");
      return;
    }
    setAmounts(amounts.filter(a => a !== valToRemove));
  };

  const handleAddSlot = () => {
    const val = parseInt(newSlotInput.trim(), 10);
    if (!val || val <= 0) return;
    if (slots.includes(val)) {
      setConfigErrorMsg(`Slot count ${val} already exists!`);
      return;
    }
    const updated = [...slots, val].sort((a, b) => a - b);
    setSlots(updated);
    setNewSlotInput('');
    setConfigErrorMsg('');
  };

  const handleRemoveSlot = (valToRemove) => {
    if (slots.length <= 1) {
      setConfigErrorMsg("At least one Slot count must be configured.");
      return;
    }
    setSlots(slots.filter(s => s !== valToRemove));
  };

  const handleResetDefaults = () => {
    setAmounts([1999, 9999, 29999, 99999]);
    setSlots([10, 20, 40, 60]);
    setFeePercent(20);
    setConfigSuccessMsg('');
    setConfigErrorMsg('');
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    setConfigSuccessMsg('');
    setConfigErrorMsg('');
    try {
      const payload = {
        amounts,
        slots,
        fee_percentage: parseFloat(feePercent) || 20
      };
      const res = await axios.post(`${constant.API_BASE_URL}/admin/lucky-bags/config`, payload, getAuthHeaders());
      if (res.data?.success) {
        setConfigSuccessMsg("Lucky Bag presets and platform fee updated successfully in system database!");
        setTimeout(() => setConfigSuccessMsg(''), 5000);
      }
    } catch (err) {
      setConfigErrorMsg(err.response?.data?.message || err.message || "Failed to update configuration.");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleViewDetail = async (bagId) => {
    setDetailLoading(true);
    setSelectedBagDetail(null);
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/admin/lucky-bags/${bagId}`, getAuthHeaders());
      if (res.data?.success && res.data?.data) {
        setSelectedBagDetail(res.data.data);
      }
    } catch (err) {
      console.error("Error loading bag details:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between pb-6 border-b border-slate-200 mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Gift className="text-rose-600" /> Lucky Bag / Red Envelopes (खुशी की पोटली)
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure dynamic Gold presets, Claim slots, Platform fee %, and monitor live room distributions.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex bg-slate-200 p-1 rounded-xl w-fit">
          <button
            onClick={() => setActiveTab('config')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === 'config'
                ? 'bg-white text-rose-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Settings className="inline-block w-4 h-4 mr-1.5" />
            Preset & Rules Config
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === 'audit'
                ? 'bg-white text-rose-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <ShieldCheck className="inline-block w-4 h-4 mr-1.5" />
            Live Audit & Bags List
          </button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Total Bags</span>
            <Gift className="w-5 h-5 text-rose-500" />
          </div>
          <div className="text-2xl font-bold text-slate-800">
            {summaryLoading ? '...' : (summary?.total_bags || 0).toLocaleString()}
          </div>
          <div className="text-xs text-emerald-600 font-medium mt-1">
            Active Now: {summary?.active_bags || 0}
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Committed Gold</span>
            <Coins className="w-5 h-5 text-amber-500" />
          </div>
          <div className="text-2xl font-bold text-amber-600">
            {summaryLoading ? '...' : (summary?.total_gold_committed || 0).toLocaleString()}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            In Circulation Pool
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Platform Fees</span>
            <ShieldCheck className="w-5 h-5 text-indigo-500" />
          </div>
          <div className="text-2xl font-bold text-indigo-600">
            {summaryLoading ? '...' : (summary?.total_platform_fees || 0).toLocaleString()}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Revenue Collected ({feePercent}%)
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between text-slate-500 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Auto-Refunded</span>
            <RotateCcw className="w-5 h-5 text-blue-500" />
          </div>
          <div className="text-2xl font-bold text-blue-600">
            {summaryLoading ? '...' : (summary?.total_refunded_gold || 0).toLocaleString()}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Unclaimed (After 24 Hours)
          </div>
        </div>
      </div>

      {/* TAB 1: PRESET & RULES CONFIG */}
      {activeTab === 'config' && (
        <div className="space-y-6">
          {configSuccessMsg && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl flex items-center gap-2 text-sm">
              <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
              <span>{configSuccessMsg}</span>
            </div>
          )}

          {configErrorMsg && (
            <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl flex items-center gap-2 text-sm">
              <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
              <span>{configErrorMsg}</span>
            </div>
          )}

          {/* Section 1: Allowed Gold Amounts */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Coins className="text-amber-500 w-5 h-5" /> Allowed Gold Presets
                </h2>
                <p className="text-xs text-slate-500">
                  Users can only choose from these approved gold amounts when sending Lucky Bags.
                </p>
              </div>
            </div>

            {/* Presets Badges */}
            <div className="flex flex-wrap gap-2.5 mb-5">
              {amounts.map((amt) => (
                <div
                  key={amt}
                  className="flex items-center gap-2 px-3.5 py-2 bg-amber-50 border border-amber-300 rounded-xl text-amber-900 font-bold text-sm shadow-xs"
                >
                  <span>{amt.toLocaleString()} Gold</span>
                  <button
                    onClick={() => handleRemoveAmount(amt)}
                    className="p-1 text-amber-600 hover:text-rose-600 hover:bg-amber-100 rounded-md transition-colors"
                    title="Remove Preset"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>

            {/* Add Preset Form */}
            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-slate-100">
              <input
                type="number"
                placeholder="Enter new gold amount (e.g. 50000)"
                value={newAmountInput}
                onChange={(e) => setNewAmountInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddAmount()}
                className="w-72 px-3.5 py-2 border border-slate-300 rounded-xl text-sm focus:outline-hidden focus:ring-2 focus:ring-rose-500"
              />
              <button
                onClick={handleAddAmount}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-sm font-semibold flex items-center gap-1.5 transition-all"
              >
                <Plus size={16} /> Add Gold Amount
              </button>
            </div>
          </div>

          {/* Section 2: Allowed Slots Counts */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Users className="text-blue-500 w-5 h-5" /> Allowed Claim Slots (Winners Count)
                </h2>
                <p className="text-xs text-slate-500">
                  Total users who can claim a random share of the reward pool.
                </p>
              </div>
            </div>

            {/* Slots Badges */}
            <div className="flex flex-wrap gap-2.5 mb-5">
              {slots.map((s) => (
                <div
                  key={s}
                  className="flex items-center gap-2 px-3.5 py-2 bg-blue-50 border border-blue-300 rounded-xl text-blue-900 font-bold text-sm shadow-xs"
                >
                  <span>{s} Users / Slots</span>
                  <button
                    onClick={() => handleRemoveSlot(s)}
                    className="p-1 text-blue-600 hover:text-rose-600 hover:bg-blue-100 rounded-md transition-colors"
                    title="Remove Slot Preset"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>

            {/* Add Slot Form */}
            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-slate-100">
              <input
                type="number"
                placeholder="Enter slot count (e.g. 30)"
                value={newSlotInput}
                onChange={(e) => setNewSlotInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddSlot()}
                className="w-72 px-3.5 py-2 border border-slate-300 rounded-xl text-sm focus:outline-hidden focus:ring-2 focus:ring-rose-500"
              />
              <button
                onClick={handleAddSlot}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-sm font-semibold flex items-center gap-1.5 transition-all"
              >
                <Plus size={16} /> Add Slot Count
              </button>
            </div>
          </div>

          {/* Section 3: Platform Fee & Live Breakdown Matrix */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800 mb-2">
              Platform Fee & Real-time Reward Pool Matrix
            </h2>
            <p className="text-xs text-slate-500 mb-4">
              Platform fee is deducted upon dispatch. The remaining {100 - feePercent}% is pre-distributed randomly across all slots.
            </p>

            <div className="flex items-center gap-4 mb-6">
              <label className="text-sm font-bold text-slate-700">Platform Fee Percentage (%):</label>
              <input
                type="number"
                min="0"
                max="99"
                value={feePercent}
                onChange={(e) => setFeePercent(Math.max(0, Math.min(99, parseFloat(e.target.value) || 0)))}
                className="w-28 px-3 py-2 border border-slate-300 rounded-xl text-sm font-bold text-center focus:ring-2 focus:ring-rose-500"
              />
              <span className="text-sm text-slate-500">
                Reward Pool Share: <strong className="text-emerald-600">{100 - feePercent}%</strong>
              </span>
            </div>

            {/* Matrix Table */}
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-100 text-slate-700 font-semibold text-xs uppercase tracking-wider">
                  <tr>
                    <th className="py-3 px-4">Gold Preset</th>
                    <th className="py-3 px-4">Platform Fee ({feePercent}%)</th>
                    <th className="py-3 px-4">Net Reward Pool ({100 - feePercent}%)</th>
                    <th className="py-3 px-4">Avg Per Slot (e.g. 20 Slots)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {amounts.map((amt) => {
                    const fee = Math.floor(amt * (feePercent / 100));
                    const pool = amt - fee;
                    return (
                      <tr key={amt} className="hover:bg-slate-50/70">
                        <td className="py-3 px-4 font-bold text-slate-800">{amt.toLocaleString()} Gold</td>
                        <td className="py-3 px-4 font-medium text-rose-600">{fee.toLocaleString()} Gold</td>
                        <td className="py-3 px-4 font-bold text-emerald-600">{pool.toLocaleString()} Gold</td>
                        <td className="py-3 px-4 text-slate-500">~{Math.floor(pool / 20).toLocaleString()} Gold/user</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Action Footer */}
          <div className="flex items-center justify-between pt-4">
            <button
              onClick={handleResetDefaults}
              className="px-4 py-2.5 border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all"
            >
              <RotateCcw size={16} /> Reset 4 Official Defaults (1999, 9999, 29999, 99999)
            </button>

            <button
              onClick={handleSaveConfig}
              disabled={savingConfig}
              className="px-6 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-sm font-bold shadow-md hover:shadow-lg flex items-center gap-2 transition-all disabled:opacity-50"
            >
              <CheckCircle size={16} /> {savingConfig ? "Saving Changes..." : "Save Configuration"}
            </button>
          </div>
        </div>
      )}

      {/* TAB 2: LIVE AUDIT & BAGS LIST */}
      {activeTab === 'audit' && (
        <div className="space-y-4">
          {/* Filters Bar */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-3 justify-between items-center">
            <div className="flex items-center gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-80">
                <Search className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by Bag ID or Sender ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchBags()}
                  className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-xl text-sm focus:outline-hidden focus:ring-2 focus:ring-rose-500"
                />
              </div>
              <button
                onClick={fetchBags}
                className="px-4 py-2 bg-slate-800 text-white rounded-xl text-sm font-medium hover:bg-slate-900"
              >
                Search
              </button>
            </div>

            <div className="flex items-center gap-2 w-full md:w-auto">
              <span className="text-xs text-slate-500 font-medium">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-slate-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-rose-500"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="completed">Fully Claimed</option>
                <option value="refunded">Refunded</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>

          {/* Bags Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-100 text-slate-700 font-semibold text-xs uppercase tracking-wider">
                  <tr>
                    <th className="py-3 px-4">Bag ID</th>
                    <th className="py-3 px-4">Room ID</th>
                    <th className="py-3 px-4">Sender</th>
                    <th className="py-3 px-4">Total Gold</th>
                    <th className="py-3 px-4">Reward Pool</th>
                    <th className="py-3 px-4">Claimed / Slots</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Created At</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {bagsLoading ? (
                    <tr>
                      <td colSpan="9" className="py-8 text-center text-slate-500">
                        Loading Lucky Bags audit records...
                      </td>
                    </tr>
                  ) : bags.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="py-8 text-center text-slate-400">
                        No Lucky Bags found matching the filters.
                      </td>
                    </tr>
                  ) : (
                    bags.map((bag) => (
                      <tr key={bag.id || bag.bag_id} className="hover:bg-slate-50/70">
                        <td className="py-3 px-4 font-mono text-xs text-slate-700">
                          {bag.bag_id ? `${bag.bag_id.substring(0, 10)}...` : bag.id}
                        </td>
                        <td className="py-3 px-4 text-xs font-mono text-slate-600">
                          {bag.room_id}
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-semibold text-slate-800">
                            {bag.first_name || bag.user_name || 'User'}
                          </div>
                          <div className="text-xs text-slate-400 font-mono">
                            {bag.sender_xoxo_id || bag.sender_id?.substring(0, 8)}
                          </div>
                        </td>
                        <td className="py-3 px-4 font-bold text-amber-600">
                          {Number(bag.total_gold || bag.total_coins || 0).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 font-semibold text-emerald-600">
                          {Number(bag.reward_pool || 0).toLocaleString()}
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-bold text-slate-800">{bag.claimed_slots || (bag.total_count - bag.remaining_count) || 0}</span>
                          <span className="text-slate-400"> / {bag.total_slots || bag.total_count || 0}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                            bag.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                            bag.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                            bag.status === 'refunded' ? 'bg-purple-100 text-purple-800' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {bag.status?.toUpperCase() || 'ACTIVE'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-500">
                          {new Date(bag.created_at).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => handleViewDetail(bag.bag_id || bag.id)}
                            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold flex items-center gap-1 ml-auto"
                          >
                            <Eye size={13} /> View Audit
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
              <div className="p-4 border-t border-slate-100 flex items-center justify-between text-sm">
                <span className="text-slate-500">
                  Page {page} of {pagination.totalPages} ({pagination.totalItems} bags)
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    className="px-3 py-1 border rounded-lg disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    disabled={page >= pagination.totalPages}
                    onClick={() => setPage(p => p + 1)}
                    className="px-3 py-1 border rounded-lg disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* DETAIL AUDIT MODAL */}
      {selectedBagDetail && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl p-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Gift className="text-rose-600" /> Lucky Bag Audit: {selectedBagDetail.bag?.bag_id}
              </h3>
              <button
                onClick={() => setSelectedBagDetail(null)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-4">
              <div className="p-3 bg-slate-50 rounded-xl">
                <div className="text-xs text-slate-500">Total Gold</div>
                <div className="text-lg font-bold text-amber-600">
                  {selectedBagDetail.bag?.total_gold?.toLocaleString()} Gold
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <div className="text-xs text-slate-500">Platform Fee (20%)</div>
                <div className="text-lg font-bold text-rose-600">
                  {selectedBagDetail.bag?.platform_fee?.toLocaleString()} Gold
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <div className="text-xs text-slate-500">Reward Pool (80%)</div>
                <div className="text-lg font-bold text-emerald-600">
                  {selectedBagDetail.bag?.reward_pool?.toLocaleString()} Gold
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <div className="text-xs text-slate-500">Remaining Slots</div>
                <div className="text-lg font-bold text-slate-800">
                  {selectedBagDetail.bag?.remaining_slots} / {selectedBagDetail.bag?.total_slots}
                </div>
              </div>
            </div>

            {/* Claims / Winners Section */}
            <h4 className="font-bold text-slate-800 text-sm mt-6 mb-2">Claimers & Winners ({selectedBagDetail.claims?.length || 0})</h4>
            <div className="overflow-x-auto border rounded-xl max-h-48 overflow-y-auto mb-6">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-100 font-semibold text-slate-600">
                  <tr>
                    <th className="p-2.5">User</th>
                    <th className="p-2.5">Reward Won</th>
                    <th className="p-2.5">Slot #</th>
                    <th className="p-2.5">Claimed At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {selectedBagDetail.claims?.length === 0 ? (
                    <tr><td colSpan="4" className="p-3 text-center text-slate-400">No claims yet.</td></tr>
                  ) : (
                    selectedBagDetail.claims?.map((c) => (
                      <tr key={c.id}>
                        <td className="p-2.5 font-semibold text-slate-800">{c.user_name || c.first_name || c.user_id}</td>
                        <td className="p-2.5 font-bold text-emerald-600">{c.claimed_amount?.toLocaleString()} Gold</td>
                        <td className="p-2.5 text-slate-500">#{c.slot_index + 1}</td>
                        <td className="p-2.5 text-slate-500">{new Date(c.created_at).toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Refund Record if any */}
            {selectedBagDetail.refund && (
              <div className="p-3.5 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-900 mb-4">
                <strong>24-Hour Auto-Refund Record:</strong> {selectedBagDetail.refund.refund_amount?.toLocaleString()} Gold refunded to sender on {new Date(selectedBagDetail.refund.created_at).toLocaleString()}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setSelectedBagDetail(null)}
                className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-sm font-semibold"
              >
                Close Audit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LuckyBagManagement;
